<template>
  <el-dialog
    v-model="visible"
    :title="t('pharmacyAssignment.title')"
    :width="isMobile ? '100%' : '600px'"
    :before-close="handleClose"
    class="pharmacy-assignment-modal"
    :z-index="10001"
  >
    <div class="pharmacy-assignment-content">
      <p class="description">{{ t('pharmacyAssignment.description') }}</p>
      
      <!-- Current Pharmacy Section -->
      <div v-if="currentPharmacy" class="current-pharmacy-section">
        <h4>{{ t('pharmacyAssignment.currentPharmacy') }}</h4>
        <div class="current-pharmacy-item">
                      <div class="pharmacy-info">
              <h5>{{ currentPharmacy.name }}</h5>
              <p class="location">{{ formatAddress(currentPharmacy) }}</p>
              <p class="contact" v-if="currentPharmacy.phone_number">
                <el-icon><Phone /></el-icon>
                {{ currentPharmacy.phone_number }}
              </p>
              <p class="npi" v-if="currentPharmacy.npi_number">
                <el-icon><Document /></el-icon>
                {{ t('pharmacies.detail.fields.licenseNumber') }}: {{ currentPharmacy.npi_number }}
              </p>
            </div>
          <div class="pharmacy-status">
            <el-tag type="success" size="small">
              {{ t('pharmacyAssignment.status.current') }}
            </el-tag>
          </div>
        </div>
        <el-button 
          type="info" 
          size="small" 
          @click="handleClearAssignment"
          :loading="clearing"
          class="clear-button"
        >
          {{ t('pharmacyAssignment.clearAssignment') }}
        </el-button>
      </div>
      
      <!-- Static Search Section -->
      <div class="search-section-static">
        <h4>{{ t('pharmacyAssignment.selectNewPharmacy') }}</h4>
        <el-input
          v-model="searchQuery"
          :placeholder="t('pharmacyAssignment.searchPlaceholder')"
          clearable
          @input="debouncedSearch"
        >
          <template #prefix>
            <el-icon><Search /></el-icon>
          </template>
        </el-input>
      </div>

      <!-- Scrollable Pharmacy List -->
      <div class="pharmacy-list-scrollable">
        <div v-if="loading" class="loading-container">
          <AppLoader 
            type="spinner" 
             
            size="large"
          />
        </div>
        <div v-else-if="pharmacies.length === 0" class="empty-state">
          <el-empty :description="t('pharmacyAssignment.noPharmacies')" />
        </div>
        
        <div v-else class="pharmacy-items">
          <div
            v-for="pharmacy in pharmacies"
            :key="pharmacy.id"
            class="pharmacy-item"
            :class="{ 'selected': selectedPharmacy?.id === pharmacy.id }"
            @click="selectPharmacy(pharmacy)"
          >
            <div class="pharmacy-info">
              <h4>{{ pharmacy.name }}</h4>
              <p class="location">{{ formatAddress(pharmacy) }}</p>
              <p class="contact" v-if="pharmacy.phone_number">
                <el-icon><Phone /></el-icon>
                {{ pharmacy.phone_number }}
              </p>
              <p class="npi" v-if="pharmacy.npi_number">
                <el-icon><Document /></el-icon>
                {{ t('pharmacies.detail.fields.licenseNumber') }}: {{ pharmacy.npi_number }}
              </p>
            </div>
            <div class="pharmacy-status">
              <el-tag :type="pharmacy.is_active ? 'success' : 'info'" size="small">
                {{ pharmacy.is_active ? t('pharmacyAssignment.status.active') : t('pharmacyAssignment.status.inactive') }}
              </el-tag>
            </div>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="dialog-footer">
        <el-button @click="handleClose">{{ t('common.cancel') }}</el-button>
        <el-button
          type="primary"
          :disabled="!selectedPharmacy"
          :loading="assigning"
          @click="handleAssign"
        >
          {{ t('pharmacyAssignment.assign') }}
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { ElNotification } from 'element-plus'
import { Search, Phone } from '@element-plus/icons-vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import apolloClient from '@/services/apollo-client'
import { PHARMACIES_QUERY } from '@/graphql/pharmacies'
import { ASSIGN_PHARMACY_MUTATION } from '@/graphql/users'
import AppLoader from '@/components/AppLoader.vue'

const { t } = useI18n()
const store = useStore()

// eslint-disable-next-line no-undef
const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  }
})

// eslint-disable-next-line no-undef
const emit = defineEmits(['update:modelValue', 'assigned'])

// Reactive data
const visible = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

// Mobile detection with reactive window resize
const windowWidth = ref(window.innerWidth)

const isMobile = computed(() => {
  return windowWidth.value <= 768
})

const handleResize = () => {
  windowWidth.value = window.innerWidth
}

onMounted(() => {
  window.addEventListener('resize', handleResize)
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
})

const loading = ref(false)
const assigning = ref(false)
const clearing = ref(false)
const pharmacies = ref([])
const searchQuery = ref('')
const selectedPharmacy = ref(null)

// Get current pharmacy from store
const currentPharmacy = computed(() => {
  const currentUser = store.getters["user/user"]
  return currentUser?.pharmacy || null
})

// Methods
const fetchPharmacies = async () => {
  loading.value = true
  try {
    const { data } = await apolloClient.query({
      query: PHARMACIES_QUERY,
      fetchPolicy: 'no-cache'
    })
    pharmacies.value = data.pharmacies || []
  } catch (error) {
    console.error('Error fetching pharmacies:', error)
    ElNotification({
      title: t('common.error'),
      message: t('pharmacyAssignment.error.fetch'),
      type: 'error',
      position: 'bottom-right'
    })
  } finally {
    loading.value = false
  }
}

const searchPharmacies = () => {
  if (!searchQuery.value) {
    fetchPharmacies()
    return
  }

  const filtered = pharmacies.value.filter(pharmacy =>
    pharmacy.name.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
    pharmacy.city?.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
    pharmacy.address_line_1?.toLowerCase().includes(searchQuery.value.toLowerCase())
  )
  
  pharmacies.value = filtered
}

// Debounced search
let searchTimeout = null
const debouncedSearch = () => {
  if (searchTimeout) clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    searchPharmacies()
  }, 300)
}

const formatAddress = (pharmacy) => {
  const parts = []
  if (pharmacy.address_line_1) parts.push(pharmacy.address_line_1)
  if (pharmacy.city) parts.push(pharmacy.city)
  if (pharmacy.state) parts.push(pharmacy.state)
  if (pharmacy.zipcode) parts.push(pharmacy.zipcode)
  
  return parts.join(', ')
}

const selectPharmacy = (pharmacy) => {
  selectedPharmacy.value = pharmacy
}

const handleClearAssignment = async () => {
  clearing.value = true
  try {
    const { data } = await apolloClient.mutate({
      mutation: ASSIGN_PHARMACY_MUTATION,
      variables: {
        input: {
          pharmacyId: null,
        },
      },
    });

    if (data.assignPharmacy.success) {
      ElNotification({
        title: t('common.success'),
        message: t('pharmacyAssignment.clearSuccess'),
        type: 'success',
        position: 'bottom-right'
      })
      
      // Update user in store
      const currentUser = store.getters["user/user"]
      const updatedUser = {
        ...currentUser,
        pharmacy_id: null,
        pharmacy: null
      }
      store.dispatch('user/setUser', updatedUser)
      
      // Redirect to admin dashboard to refresh all services
      window.location.href = '/dashboard'
    } else {
      ElNotification({
        title: t('common.error'),
        message: data.assignPharmacy.message || t('pharmacyAssignment.error.clear'),
        type: 'error',
        position: 'bottom-right'
      })
    }
  } catch (error) {
    console.error('Error clearing pharmacy assignment:', error)
    ElNotification({
      title: t('common.error'),
      message: t('pharmacyAssignment.error.clear'),
      type: 'error',
      position: 'bottom-right'
    })
  } finally {
    clearing.value = false
  }
}

const handleAssign = async () => {
  if (!selectedPharmacy.value) return

  assigning.value = true
  try {
    const { data } = await apolloClient.mutate({
      mutation: ASSIGN_PHARMACY_MUTATION,
      variables: {
        input: {
          pharmacyId: selectedPharmacy.value.id
        }
      }
    })

    if (data.assignPharmacy.success) {
      ElNotification({
        title: t('common.success'),
        message: t('pharmacyAssignment.success'),
        type: 'success',
        position: 'bottom-right'
      })
      
      // Update user in store with data from response
      const currentUser = store.getters["user/user"]
      const responseData = data.assignPharmacy.data
      const updatedUser = {
        ...currentUser,
        pharmacy_id: responseData?.pharmacyId || selectedPharmacy.value.id,
        pharmacy: responseData?.pharmacy || selectedPharmacy.value
      }
      store.dispatch('user/setUser', updatedUser)
      
      // Redirect to pharmacy dashboard to refresh all services
      window.location.href = `/pharmacies/${selectedPharmacy.value.id}/dashboard`
      handleClose()
    } else {
      ElNotification({
        title: t('common.error'),
        message: data.assignPharmacy.message || t('pharmacyAssignment.error.assign'),
        type: 'error',
        position: 'bottom-right'
      })
    }
  } catch (error) {
    console.error('Error assigning pharmacy:', error)
    ElNotification({
      title: t('common.error'),
      message: t('pharmacyAssignment.error.assign'),
      type: 'error',
      position: 'bottom-right'
    })
  } finally {
    assigning.value = false
  }
}

const handleClose = () => {
  visible.value = false
  selectedPharmacy.value = null
  searchQuery.value = ''
}

// Watch for modal visibility
watch(visible, (newValue) => {
  if (newValue) {
    fetchPharmacies()
  }
})
</script>

<style scoped>
.pharmacy-assignment-content {
  display: flex;
  flex-direction: column;
  height: 500px;
}

.description {
  margin-bottom: 16px;
  color: #606266;
  line-height: 1.4;
  font-size: 14px;
}

.current-pharmacy-section {
  margin-bottom: 20px;
  padding: 12px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e4e7ed;
}

.current-pharmacy-section h4 {
  margin: 0 0 12px 0;
  color: #303133;
  font-size: 14px;
  font-weight: 600;
}

.current-pharmacy-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  background-color: white;
  border-radius: 6px;
  border: 1px solid #e4e7ed;
  margin-bottom: 12px;
}

.current-pharmacy-item .pharmacy-info h5 {
  margin: 0 0 4px 0;
  color: #303133;
  font-size: 14px;
  font-weight: 600;
}

.clear-button {
  margin-top: 8px;
}

/* Static search section - doesn't scroll */
.search-section-static {
  margin-bottom: 16px;
  flex-shrink: 0;
}

.search-section-static h4 {
  margin: 0 0 12px 0;
  color: #303133;
  font-size: 14px;
  font-weight: 600;
}

/* Scrollable pharmacy list */
.pharmacy-list-scrollable {
  flex: 1;
  overflow-y: auto;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.loading-container {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
}

.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
}

.pharmacy-items {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.pharmacy-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.pharmacy-item:hover {
  border-color: #409eff;
  background-color: #f0f9ff;
}

.pharmacy-item.selected {
  border-color: #409eff;
  background-color: #e6f7ff;
}

.pharmacy-info h4 {
  margin: 0 0 6px 0;
  color: #303133;
  font-size: 15px;
}

.pharmacy-info p {
  margin: 3px 0;
  color: #606266;
  font-size: 13px;
}

.pharmacy-info .location {
  font-weight: 500;
}

.pharmacy-info .contact {
  display: flex;
  align-items: center;
  gap: 4px;
}

.pharmacy-status {
  display: flex;
  align-items: center;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

/* Z-index fixes for impersonation modal */
:deep(.pharmacy-assignment-modal) {
  z-index: 10001 !important;
}

:deep(.pharmacy-assignment-modal .el-dialog__wrapper) {
  z-index: 10001 !important;
}

:deep(.pharmacy-assignment-modal .el-overlay) {
  z-index: 10000 !important;
}

/* Mobile responsive styles */
@media (max-width: 768px) {
  :deep(.pharmacy-assignment-modal) {
    width: 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
    margin: 0 !important;
    height: 100vh !important;
    min-height: 100vh !important;
    border-radius: 0 !important;
    --el-dialog-width: 100% !important;
    --el-dialog-min-height: 100vh !important;
  }
  
  .pharmacy-assignment-content {
    height: 100%;
    max-height: 100%;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
  
  .current-pharmacy-section {
    margin-bottom: 16px;
    padding: 8px;
  }
  
  .current-pharmacy-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  
  .current-pharmacy-item .pharmacy-status {
    align-self: flex-end;
  }
  
  .search-section-static {
    margin-bottom: 12px;
  }
  
  .pharmacy-list-scrollable {
    min-height: 200px;
    height: 100%;
    overflow-y: auto;
    flex: 1;
    -webkit-overflow-scrolling: touch;
  }
  
  .pharmacy-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    padding: 10px;
  }
  
  .pharmacy-item .pharmacy-status {
    align-self: flex-end;
  }
  
  .pharmacy-info h4 {
    font-size: 14px;
  }
  
  .pharmacy-info p {
    font-size: 12px;
  }
  
  .dialog-footer {
    flex-direction: row;
    gap: 12px;
    justify-content: flex-end;
  }
  
  .dialog-footer .el-button {
    flex: 1;
    max-width: 150px;
  }
}
</style> 